import throttle from "lodash.throttle";
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import { Events } from "~/constants/events";
import { isDescendant } from "~/utils/dom/isDescendant";

const THROTTLE_SCROLL_PERIOD = 500;

interface UseHovered<T> {
  hovered: boolean;
  ref?: MutableRefObject<T | null>;
  setHovered: Dispatch<SetStateAction<boolean>>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

type UseHoveredProps = {
  useRefToScrollDetection?: boolean;
  detect?: boolean;
};

export function useHovered<T extends HTMLElement = HTMLDivElement>({
  useRefToScrollDetection = false,
  detect = true
}: UseHoveredProps): UseHovered<T> {
  const [hovered, setHovered] = useState(false);
  const hostRef = useRef<null | T>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    throttle(
      (event: WheelEvent) => {
        const elementUnderCursor = document.elementFromPoint(
          event.clientX + event.deltaX,
          event.clientY + event.deltaY
        );

        const scrollingInBoundariesOfHost =
          elementUnderCursor?.isSameNode(hostRef.current) ||
          isDescendant(hostRef.current, elementUnderCursor);

        if (!scrollingInBoundariesOfHost) {
          setHovered(false);
        }
      },
      THROTTLE_SCROLL_PERIOD,
      { leading: true, trailing: true }
    ),
    []
  );

  useEffect(() => {
    if (!useRefToScrollDetection || !detect) {
      return;
    }

    if (hovered) {
      window.addEventListener(Events.wheel, handleScroll);
    }

    return () => {
      window.removeEventListener(Events.wheel, handleScroll);
    };
  }, [detect, handleScroll, hovered, useRefToScrollDetection]);

  const handleMouseEnter = (): void => {
    setHovered(true);
  };

  const handleMouseLeave = (): void => {
    setHovered(false);
  };

  return {
    hovered,
    setHovered,
    ...(useRefToScrollDetection ? { ref: hostRef } : null),
    onMouseEnter: detect ? handleMouseEnter : () => void 0,
    onMouseLeave: detect ? handleMouseLeave : () => void 0
  };
}
